/**
 * @fileoverview AR-924 — In-memory state behind the mock port: one delivery per (activity, student).
 * Derives `late` the way the backend does for teacher uploads (`work_completed_at > task.date`).
 */

import type { TaskTrackDeliverable, TaskTrackDelivery } from '../../types'

export const MOCK_CURRENT_TEACHER = { id: 'mock-teacher-you', name: 'You', currentUser: true }

const store = new Map<string, TaskTrackDelivery>()
const keyOf = (activityId: string, studentId: string) => `${activityId}:${studentId}`
let sequence = 0

export const nextMockId = (prefix: string): string => `${prefix}-${++sequence}`

export function getMockDelivery(activityId: string, studentId: string): TaskTrackDelivery | undefined {
  return store.get(keyOf(activityId, studentId))
}

export function seedMockDelivery(activityId: string, studentId: string, delivery: TaskTrackDelivery | null): void {
  if (delivery) store.set(keyOf(activityId, studentId), delivery)
  else store.delete(keyOf(activityId, studentId))
}

export function resetMockDeliveries(): void {
  store.clear()
}

/** Like the backend, a new teacher delivery has no work-completed date until the PATCH sets one. */
export function emptyTeacherDelivery(workCompletedAt: string | null = null): TaskTrackDelivery {
  return {
    id: nextMockId('mock-delivery'),
    status: 'done',
    description: '',
    note: '',
    submittedAt: new Date().toISOString(),
    late: false,
    workCompletedAt,
    uploadedBy: MOCK_CURRENT_TEACHER,
    files: [],
    links: [],
    source: 'platform',
  }
}

function isLateTeacherUpload(delivery: TaskTrackDelivery, taskDate: string): boolean {
  if (!delivery.workCompletedAt || !taskDate) return false
  return delivery.workCompletedAt > taskDate.slice(0, 10)
}

export function withDelivery(base: TaskTrackDeliverable, delivery: TaskTrackDelivery | null): TaskTrackDeliverable {
  if (!delivery) return { ...base, delivery: null, deliveryStatus: 'not_submitted' }
  const late = delivery.uploadedBy ? isLateTeacherUpload(delivery, base.task.date) : delivery.late
  return { ...base, delivery: { ...delivery, late }, deliveryStatus: late ? 'late' : 'on_time' }
}

/** Removing the last file/link returns the student to "not submitted" (backend `destroy_if_empty`). */
export function dropIfEmpty(activityId: string, studentId: string, delivery: TaskTrackDelivery): void {
  const empty = delivery.files.length === 0 && (delivery.links ?? []).length === 0
  seedMockDelivery(activityId, studentId, empty ? null : delivery)
}
